import { useMemo, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react'
import type { BalanceMatrixRow, LeaveCreditsLeaveType } from '@lms/shared-types'
import { Tooltip } from '../../components/primitives/Tooltip'
import { getBalancesMatrix } from '../../api/leave-credits'
import { useDebounce } from '../../lib/use-debounce'
import { useUrlSearch } from '../../lib/use-url-search'
import { flags } from '../../lib/feature-flags'
import { LEAVE_TYPE_STYLE } from '../../lib/leave-type-palette'
import { LEAVE_TYPE_SHORT } from '../../lib/leave-type-meta'
import { usePagedList, type PagedFetchArgs, type PagedFetchResult } from '../../lib/use-paged-list'
import { PaginationControls } from '../../components/lists/PaginationControls'
import { BalancesDetailModal } from './BalancesDetailModal'
import { BalancesExportButton } from './BalancesExportButton'
import {
  columnLeaveTypesFor,
  computeMatrixCell,
  fmtNum,
  type RosterMetric,
} from './matrix-cell'

/**
 * Shared wide-roster used by both the Balances and Entitlement tabs: one row
 * per employee, one column per leave type, backed by
 * GET /leave-credits/balances/matrix (numbered/offset pagination via
 * `?page=&limit=`). Clicking a row opens the detail pop-up (full
 * entitled/used/pending/remaining + expiry); the per-row Adjust button opens
 * it straight in edit mode.
 *
 * `metric` picks what each cell shows:
 *   • 'remaining' — bookable balance (Balances tab). Unpaid, being uncapped,
 *     shows days *taken* instead (red when > 0).
 *   • 'entitled'  — the year's entitlement (Entitlement tab). Unpaid is 0.
 * A genuine gender gate (maternity/paternity for the other gender) shows "—";
 * a zero value on an applicable type is shown as a real number.
 */

const CURRENT_YEAR = new Date().getFullYear()

// Pin every matrix row to the Applied Leaves / Employees row height so all the
// Leaves tabs line up. Those rows are sized by a 32px action control sitting in
// `.tbl td`'s 12px vertical padding (32 + 24 = 56). Here the Balance rows are
// text-only and the Entitlement rows carry a shorter Adjust button, so without
// a fixed height they read as more compact; `.tbl td { vertical-align: middle }`
// centres the content within it.
const ROW_HEIGHT_PX = 56

// The cell/column display logic lives in `matrix-cell` so this table and the CSV
// export produce identical numbers. Re-exported here for existing consumers
// (BalancesTab props, the columns unit test) that import from this module.
export { columnLeaveTypesFor } from './matrix-cell'
export type { RosterMetric } from './matrix-cell'

// Filter-row styles shared with the Applications tab (mono uppercase label
// above a bordered input).
const filterBarStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: 12,
  marginBottom: 16,
}
const filterLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-3)',
}
const filterControlStyle: CSSProperties = {
  minHeight: 36,
  width: '100%',
  boxSizing: 'border-box',
  padding: '6px 10px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
  fontWeight: 400,
  textTransform: 'none',
  letterSpacing: 'normal',
}

export interface MatrixRosterProps {
  metric: RosterMetric
  /** Prefixes the search / row / adjust test ids (e.g. "balances", "entitlement"). */
  testIdPrefix: string
  searchPlaceholder?: string
  /** Optional controls rendered to the right of the search (e.g. Defaults). */
  actions?: ReactNode
}

export function MatrixRoster({
  metric,
  testIdPrefix,
  searchPlaceholder = 'Employee name…',
  actions,
}: MatrixRosterProps): JSX.Element {
  // The employee-name filter lives on the URL (`?q=`) so it persists across the
  // hub's tabs and survives the detail/adjust modal opening. Shared key with
  // the Applied Leaves tab, so one search carries across all three.
  const [search, setSearch] = useUrlSearch({ to: '/hr/leaves' })
  // Track the open detail by employee id (not a row snapshot) so the modal
  // always reflects the LIVE roster data — after an adjustment or reset
  // invalidates the query, the derived row carries the fresh override id.
  const [detailEmpId, setDetailEmpId] = useState<string | null>(null)
  const [startEdit, setStartEdit] = useState(false)
  const debouncedSearch = useDebounce(search, 250)
  // Export is a read-only, flag-gated affordance on the Leave Remaining Balance
  // view only (the entitlement view has its own Adjust/Defaults actions).
  const showExport = metric === 'remaining' && flags.balancesExport

  const openDetail = (row: BalanceMatrixRow, edit: boolean): void => {
    setStartEdit(edit)
    setDetailEmpId(row.employeeId)
  }

  // Numbered (offset) fetcher — the API is offset-capable via `?page=`, and
  // returns an exact `total`, so it maps straight onto usePagedList.
  const pagedFetcher = useMemo(
    () =>
      async ({ page, pageSize }: PagedFetchArgs): Promise<PagedFetchResult<BalanceMatrixRow>> => {
        const params: Parameters<typeof getBalancesMatrix>[0] = {
          year: CURRENT_YEAR,
          limit: pageSize,
          page,
        }
        if (debouncedSearch) params.q = debouncedSearch
        const r = await getBalancesMatrix(params)
        return { rows: r.items, total: r.total ?? 0 }
      },
    [debouncedSearch],
  )

  const pagedList = usePagedList<BalanceMatrixRow>({
    // Year is fixed to the current year here; search is the only filter, so it
    // keys the reset-to-page-1 on search change.
    queryKey: ['balances-matrix', CURRENT_YEAR, debouncedSearch],
    fetcher: pagedFetcher,
  })

  const { rows, isLoading } = pagedList
  // Derive the open row from the live rows so a post-mutation refetch flows
  // through to the modal (fresh override id) without a page reload.
  const detailRow =
    detailEmpId != null
      ? (rows.find((r) => r.employeeId === detailEmpId) ?? null)
      : null

  return (
    <>
      <div style={filterBarStyle}>
        <label style={{ ...filterLabelStyle, flex: 1 }}>
          Search
          <input
            type="search"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={`${testIdPrefix}-search`}
            style={filterControlStyle}
          />
        </label>
        {actions || showExport ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            {actions}
            {/* Export lives only on the read-only Leave Remaining Balance view
                (flag-gated); it pulls every employee (all pages), matching the
                current search. */}
            {showExport ? (
              <BalancesExportButton
                metric={metric}
                year={CURRENT_YEAR}
                search={debouncedSearch}
                testIdPrefix={testIdPrefix}
              />
            ) : null}
          </div>
        ) : null}
      </div>

      {!isLoading && rows.length === 0 ? (
        <div className="placeholder" data-testid={`${testIdPrefix}-empty`}>
          {search ? 'No employees match that search.' : 'No employees to show.'}
        </div>
      ) : (
        <div className="card" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {/* Fixed layout so the matrix fits one view without horizontal scroll.
              The Employee column takes a fixed share and truncates; the numeric
              leave-type columns distribute the rest equally (values are short). */}
          <table className="tbl" style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ width: '18%' }}>Employee</th>
                {columnLeaveTypesFor(metric).map((lt) => (
                  <th key={lt} style={{ textAlign: 'right' }}>
                    <Tooltip
                      content={
                        lt === 'unpaid' && metric === 'remaining'
                          ? 'Unpaid (taken)'
                          : LEAVE_TYPE_STYLE[lt].label
                      }
                      delay={0}
                    >
                      <span
                        style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, cursor: 'default' }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                          <span
                            aria-hidden="true"
                            style={{ width: 8, height: 8, borderRadius: '50%', background: LEAVE_TYPE_STYLE[lt].swatch }}
                          />
                          {LEAVE_TYPE_SHORT[lt]}
                        </span>
                        {/* Unpaid has no entitlement — the column shows days taken,
                            so label it inline (not just in the tooltip). */}
                        {lt === 'unpaid' ? (
                          <span
                            style={{ fontSize: 9.5, fontWeight: 400, color: 'var(--text-3)', textTransform: 'none', letterSpacing: 0, lineHeight: 1 }}
                          >
                            (taken)
                          </span>
                        ) : null}
                      </span>
                    </Tooltip>
                  </th>
                ))}
                {/* Adjust is an entitlement action only — the remaining view
                    (Leave Remaining Balance) is read-only, no Adjust column. */}
                {metric === 'entitled' ? <th aria-label="Adjust" /> : null}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan={columnLeaveTypesFor(metric).length + (metric === 'entitled' ? 2 : 1)}
                    style={{ textAlign: 'center', color: 'var(--text-3)' }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <RosterRow
                    key={row.employeeId}
                    row={row}
                    metric={metric}
                    testIdPrefix={testIdPrefix}
                    onOpen={() => openDetail(row, false)}
                    onAdjust={() => openDetail(row, true)}
                  />
                ))
              )}
            </tbody>
          </table>
          <PaginationControls
            page={pagedList.page}
            pageSize={pagedList.pageSize}
            total={pagedList.total}
            totalPages={pagedList.totalPages}
            pageSizeOptions={pagedList.pageSizeOptions}
            rangeStart={pagedList.rangeStart}
            rangeEnd={pagedList.rangeEnd}
            canPrev={pagedList.canPrev}
            canNext={pagedList.canNext}
            onPageChange={pagedList.setPage}
            onPageSizeChange={pagedList.setPageSize}
            isFetching={pagedList.isFetching}
          />
        </div>
      )}

      <BalancesDetailModal
        row={detailRow}
        startInEdit={startEdit}
        onClose={() => setDetailEmpId(null)}
      />
    </>
  )
}

function RosterRow({
  row,
  metric,
  testIdPrefix,
  onOpen,
  onAdjust,
}: Readonly<{
  row: BalanceMatrixRow
  metric: RosterMetric
  testIdPrefix: string
  onOpen: () => void
  onAdjust: () => void
}>): JSX.Element {
  const onKey = (e: KeyboardEvent<HTMLTableRowElement>): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpen()
    }
  }
  return (
    <tr
      data-testid={`${testIdPrefix}-row-${row.employeeId}`}
      tabIndex={0}
      role="button"
      aria-label={`Open ${row.employeeName}'s ${metric === 'entitled' ? 'entitlement' : 'balance'} breakdown`}
      onClick={onOpen}
      onKeyDown={onKey}
      style={{ cursor: 'pointer', height: ROW_HEIGHT_PX }}
    >
      <td style={{ fontWeight: 500 }}>
        <span
          title={row.employeeName}
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
          }}
        >
          {row.employeeName}
        </span>
      </td>
      {columnLeaveTypesFor(metric).map((lt) => {
        // Single-sourced with the CSV export via `computeMatrixCell`, so the
        // downloaded file matches this cell for cell (accrual-stage annual,
        // unpaid-taken, gender gates all included).
        const { applicable, value, unpaidTaken, accrualNegative } = computeMatrixCell(
          row,
          lt as LeaveCreditsLeaveType,
          metric,
        )
        return (
          <td
            key={lt}
            style={{
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              color:
                !applicable
                  ? 'var(--text-3)'
                  : unpaidTaken || accrualNegative
                    ? 'var(--danger-fg)'
                    : 'var(--text)',
              fontWeight: unpaidTaken || accrualNegative ? 600 : undefined,
            }}
            title={
              applicable
                ? unpaidTaken
                  ? 'Unpaid days taken (no entitlement cap)'
                  : accrualNegative
                    ? 'Overdrawn — booked more annual leave than accrued so far'
                    : undefined
                : 'Not applicable for this employee'
            }
          >
            {applicable ? fmtNum(value) : '—'}
          </td>
        )
      })}
      {/* Adjust only in the entitlement view; the remaining view is read-only. */}
      {metric === 'entitled' ? (
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="btn btn-sm"
            data-testid={`${testIdPrefix}-adjust-row-${row.employeeId}`}
            title={`Adjust ${row.employeeName}'s balances`}
            style={{
              background: 'var(--surface)',
              color: 'var(--steel-blue)',
              border: '1px solid var(--steel-blue)',
            }}
            onClick={(e) => {
              e.stopPropagation()
              onAdjust()
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') e.stopPropagation()
            }}
          >
            Adjust
          </button>
        </td>
      ) : null}
    </tr>
  )
}
